/**
 * Login, registration, and forgot-password (Djoser reset email).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import GoogleSignInButton from '../components/GoogleSignInButton';
import TurnstileWidget from '../components/TurnstileWidget';
import { trackGaEvent, trackSignUp } from '../utils/analytics';
import { POST_SIGNUP } from '../utils/paths';
import './Login.css';

const GOOGLE_ERROR_KEYS = {
  google_disabled: 'login.errGoogleUnavailable',
  google_unavailable: 'login.errGoogleUnavailable',
  missing_credential: 'login.errGoogle',
  invalid_token: 'login.errGoogle',
  email_unverified: 'login.errGoogleUnverified',
  no_email: 'login.errGoogleNoEmail',
  account_disabled: 'login.errAccountDisabled',
  create_failed: 'login.errGoogle',
  google_auth_failed: 'login.errGoogle',
  rate_limited: 'login.errRateLimited',
};

const isRegisterMode = (searchParams) => {
  const mode = (searchParams.get('mode') || '').toLowerCase();
  const signup = (searchParams.get('signup') || '').toLowerCase();
  return mode === 'register' || mode === 'signup' || signup === '1' || signup === 'true';
};

const isForgotMode = (searchParams) =>
  (searchParams.get('mode') || '').toLowerCase() === 'forgot';

const Login = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    re_password: '',
  });
  const [isLogin, setIsLogin] = useState(!isRegisterMode(searchParams) && !isForgotMode(searchParams));
  const [isForgot, setIsForgot] = useState(isForgotMode(searchParams));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingActivationEmail, setPendingActivationEmail] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const { login, loginWithGoogle, register } = useAuth();
  const { t, lang, localePath } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (() => {
    const next = searchParams.get('next') || '';
    if (next.startsWith('/') && !next.startsWith('//')) {
      return next;
    }
    const from = location.state?.from;
    if (!from?.pathname) return '/dashboard';
    if (from.pathname.startsWith('/login')) return '/dashboard';
    return `${from.pathname}${from.search || ''}`;
  })();

  const friendlyError = (message) => {
    if (!message || typeof message !== 'string') return t('login.errGeneric');
    const m = message.toLowerCase();
    if (m.includes('email') && (m.includes('already exists') || m.includes('already registered'))) {
      return t('login.errEmailExists');
    }
    if (
      m.includes('no active account') ||
      m.includes('inactive') ||
      m.includes('not active') ||
      m.includes('user is inactive')
    ) {
      return t('login.errInactive');
    }
    if (m.includes('invalid') && (m.includes('credential') || m.includes('password'))) {
      return googleClientId ? t('login.errCredentialsGoogle') : t('login.errCredentials');
    }
    if (m.includes('password') && (m.includes('too short') || m.includes('common') || m.includes('similar'))) {
      return t('login.errWeakPassword');
    }
    if (m.includes('password') && m.includes('match')) {
      return t('login.errPasswordMatch');
    }
    if (m.includes('google')) {
      return t('login.errGoogle');
    }
    return message;
  };

  useEffect(() => {
    const baked = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
    const publicApi = axios.create({ baseURL: apiBase });
    publicApi
      .get('/api/auth/google/config/')
      .then(({ data }) => {
        const clientId = (data?.client_id || data?.clientId || '').trim();
        if ((data?.enabled && clientId) || baked) {
          setGoogleClientId(baked || clientId);
        }
        const siteKey = (data?.turnstile_site_key || '').trim();
        if (data?.turnstile_enabled && siteKey) {
          setTurnstileSiteKey(siteKey);
        }
      })
      .catch((err) => {
        console.error('Auth config failed:', err);
        if (baked) {
          setGoogleClientId(baked);
        }
      });
    return undefined;
  }, []);

  useEffect(() => {
    const forgot = isForgotMode(searchParams);
    setIsForgot(forgot);
    setIsLogin(!isRegisterMode(searchParams) && !forgot);
  }, [searchParams]);

  const switchMode = (mode) => {
    setError('');
    setSuccess('');
    setPendingActivationEmail('');
    if (mode === 'login') {
      setIsLogin(true);
      setIsForgot(false);
      setSearchParams({}, { replace: true });
    } else if (mode === 'register') {
      setIsLogin(false);
      setIsForgot(false);
      setTurnstileToken('');
      setSearchParams({ mode: 'register' }, { replace: true });
    } else if (mode === 'forgot') {
      setIsLogin(false);
      setIsForgot(true);
      setSearchParams({ mode: 'forgot' }, { replace: true });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(
        '/api/auth/users/reset_password/',
        { email: formData.email },
        {
          headers: {
            'Accept-Language': lang === 'en' ? 'en' : 'es',
            'X-ClarCV-Lang': lang === 'en' ? 'en' : 'es',
          },
        }
      );
      setSuccess(t('login.resetSent'));
    } catch (err) {
      if (err.response?.status === 204 || err.response?.status === 200) {
        setSuccess(t('login.resetSent'));
      } else if (!err.response) {
        setError(t('login.errUnexpected'));
      } else {
        setSuccess(t('login.resetSent'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          navigate(returnTo, { replace: true });
        } else {
          setError(friendlyError(result.error));
        }
      } else {
        if (formData.password !== formData.re_password) {
          setError(t('login.errPasswordMatch'));
          setLoading(false);
          return;
        }
        if (turnstileSiteKey && !turnstileToken) {
          setError(t('login.errCaptcha'));
          setLoading(false);
          return;
        }
        const payload = {
          ...formData,
          username: formData.email || 'user',
          first_name: '',
          last_name: '',
          turnstile_token: turnstileToken,
        };
        const result = await register(payload);
        if (result.success) {
          const email = formData.email;
          trackSignUp('email');
          setPendingActivationEmail(email);
          setSuccess(t('login.successRegister', { email }));
          setTurnstileToken('');
          setFormData({
            email,
            password: '',
            re_password: '',
          });
        } else {
          setTurnstileToken('');
          setError(friendlyError(result.error));
        }
      }
    } catch (err) {
      setError(friendlyError(t('login.errUnexpected')));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        const result = await loginWithGoogle(credential);
        if (result.success) {
          if (result.created) {
            trackSignUp('google');
            navigate(POST_SIGNUP, { replace: true });
          } else {
            trackGaEvent('login', { method: 'google' });
            navigate(returnTo, { replace: true });
          }
        } else {
          const key = GOOGLE_ERROR_KEYS[result.errorCode];
          setError(key ? t(key) : t('login.errGoogle'));
        }
      } catch (err) {
        setError(friendlyError(t('login.errGoogle')));
      } finally {
        setLoading(false);
      }
    },
    [loginWithGoogle, navigate, returnTo, t]
  );

  const showCheckEmail = Boolean(pendingActivationEmail);

  const title = showCheckEmail
    ? t('login.successRegisterTitle')
    : isForgot
      ? t('login.titleForgot')
      : isLogin
        ? t('login.titleLogin')
        : t('login.titleRegister');
  const lead = showCheckEmail
    ? t('login.successRegister', { email: pendingActivationEmail })
    : isForgot
      ? t('login.leadForgot')
      : isLogin
        ? t('login.leadLogin')
        : t('login.leadRegister');

  return (
    <div className="auth-page">
      <Seo title={title} description={lead} path="/login" noindex />
      <div className="auth-card">
        <h1 className="auth-card__title">{title}</h1>
        <p className="auth-card__lead">{lead}</p>

        {showCheckEmail ? (
          <div className="auth-check-email">
            <div className="auth-alert auth-alert--info" role="status">
              <span>{t('login.successRegisterStepsTitle')}</span>
            </div>
            <ol className="auth-check-email__steps">
              <li>{t('login.successRegisterStep1')}</li>
              <li>{t('login.successRegisterStep2')}</li>
              <li>{t('login.successRegisterStep3')}</li>
              <li>{t('login.successRegisterStep4')}</li>
            </ol>
            <p className="auth-check-email__hint">{t('login.successRegisterHint')}</p>
            <button
              type="button"
              className="btn btn-primary auth-card__submit"
              onClick={() => switchMode('login')}
            >
              {t('login.goToLoginAfterRegister')}
            </button>
          </div>
        ) : isForgot ? (
          <form onSubmit={handleForgotSubmit}>
            <div className="form-group">
              <label htmlFor="email">{t('login.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>
            {error && (
              <div className="auth-alert auth-alert--error" role="alert">
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="auth-alert auth-alert--success" role="status">
                <span>{success}</span>
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary auth-card__submit"
              disabled={loading}
            >
              {loading ? t('login.submitting') : t('login.submitForgot')}
            </button>
          </form>
        ) : (
          <>
            {error && (
              <div className="auth-alert auth-alert--error" role="alert">
                <span>{error}</span>
              </div>
            )}
            {success && !showCheckEmail && (
              <div className="auth-alert auth-alert--success" role="status">
                <span>{success}</span>
              </div>
            )}
            {googleClientId && !googleUnavailable ? (
              <>
                <GoogleSignInButton
                  clientId={googleClientId}
                  onCredential={handleGoogleCredential}
                  onUnavailable={() => setGoogleUnavailable(true)}
                  disabled={loading}
                  locale={lang}
                  text={isLogin ? 'signin_with' : 'signup_with'}
                />
                <div className="auth-divider">{t('login.orEmail')}</div>
              </>
            ) : null}
            {googleUnavailable ? (
              <p className="auth-card__lead">{t('login.errGoogleUnavailable')}</p>
            ) : null}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">{t('login.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('login.password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                data-clarity-mask="true"
                value={formData.password}
                onChange={handleChange}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                required
              />
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="re_password">{t('login.confirmPassword')}</label>
                <input
                  type="password"
                  id="re_password"
                  name="re_password"
                  data-clarity-mask="true"
                  value={formData.re_password || ''}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
              </div>
            )}

            {!isLogin && turnstileSiteKey ? (
              <TurnstileWidget
                siteKey={turnstileSiteKey}
                language={lang === 'en' ? 'en' : 'es'}
                onToken={(token) => {
                  setTurnstileToken(token);
                  setError('');
                }}
                onExpire={() => setTurnstileToken('')}
                onError={() => {
                  setTurnstileToken('');
                  setError(t('login.errCaptcha'));
                }}
              />
            ) : null}

            <button
              type="submit"
              className="btn btn-primary auth-card__submit"
              disabled={loading || (!isLogin && Boolean(turnstileSiteKey) && !turnstileToken)}
            >
              {loading
                ? t('login.submitting')
                : isLogin
                  ? t('login.submitLogin')
                  : t('login.submitRegister')}
            </button>
          </form>
          </>
        )}

        {!showCheckEmail && (
          <div className="auth-card__switch">
            {isForgot ? (
              <button type="button" className="auth-card__switch-btn" onClick={() => switchMode('login')}>
                {t('login.switchToLogin')}
              </button>
            ) : (
              <>
                {isLogin && (
                  <button
                    type="button"
                    className="auth-card__switch-btn auth-card__forgot"
                    onClick={() => switchMode('forgot')}
                  >
                    {t('login.forgotLink')}
                  </button>
                )}
                <button
                  type="button"
                  className="auth-card__switch-btn"
                  onClick={() => switchMode(isLogin ? 'register' : 'login')}
                >
                  {isLogin ? t('login.switchToRegister') : t('login.switchToLogin')}
                </button>
              </>
            )}
          </div>
        )}

        <Link to="/" className="auth-card__back">
          ← {t('footer.home')}
        </Link>

        <p className="auth-card__legal">
          {t('login.privacyNote')} <Link to={localePath('/privacy')}>{t('login.privacy')}</Link>{' '}
          {t('login.termsNote')} <Link to={localePath('/terms')}>{t('login.terms')}</Link>.
        </p>
      </div>
    </div>
  );
};

export default Login;
